import type { CosmeticItem } from './shopApi';
import { resolveAssetUrl } from './assetUrl';
import { FINISHER_META } from '@/game/fx/finishers';

/**
 * Procedural icon for a cosmetic — we don't have art assets yet, so we render
 * a slot-shaped SVG tinted with the cosmetic's `data.tint` (or palette default).
 * Victory Shout slot shows the shout text instead of an icon.
 */
export function CosmeticPreview({ item, size = 56 }: { item: CosmeticItem; size?: number }) {
  // Admin-uploaded icon takes priority — falls back to procedural SVG so
  // catalog entries without art still render something recognizable.
  const iconUrl = resolveAssetUrl(item.iconUrl);
  if (item.slot === 'matchBackground') {
    const rawImage = (item.data?.imageUrl as string | undefined) ?? item.iconUrl;
    const backgroundImage = resolveAssetUrl(rawImage);
    const background = (item.data?.background as string | undefined)
      ?? 'linear-gradient(180deg, #1E1B4B, #050314)';
    const accent = (item.data?.accent as string | undefined) ?? '#22D3EE';
    return (
      <div
        title={item.name}
        style={{
          width: size,
          height: size,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 10,
          border: `1px solid ${accent}88`,
          background: backgroundImage
            ? `linear-gradient(rgba(5,3,20,.12), rgba(5,3,20,.48)), url("${backgroundImage}") center / cover no-repeat`
            : background,
          boxShadow: `inset 0 0 16px rgba(0,0,0,.35), 0 0 10px ${accent}22`,
          pointerEvents: 'none',
        }}
      >
        <span style={{ position: 'absolute', left: '32%', top: 7, bottom: 7, width: 1, background: 'rgba(255,255,255,.22)' }} />
        <span style={{ position: 'absolute', right: '32%', top: 7, bottom: 7, width: 1, background: 'rgba(255,255,255,.22)' }} />
        <span style={{ position: 'absolute', left: '44%', bottom: 12, width: 7, height: 7, borderRadius: '50%', background: '#E63946', boxShadow: '0 0 6px #E63946' }} />
        <span style={{ position: 'absolute', right: '42%', top: 15, width: 7, height: 7, borderRadius: '50%', background: '#3B82F6', boxShadow: '0 0 6px #3B82F6' }} />
      </div>
    );
  }
  if (iconUrl) {
    return (
      <img
        src={iconUrl}
        alt={item.name}
        width={size}
        height={size}
        draggable={false}
        style={{
          width: size,
          height: size,
          objectFit: 'contain',
          imageRendering: 'auto',
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
          pointerEvents: 'none',
        }}
      />
    );
  }

  const tint =
    (item.data?.tint as string | undefined) ??
    (item.slot === 'avatarFrame'
      ? (item.data?.border as string | undefined) ?? '#22D3EE'
      : '#9B5DE5');

  if (item.slot === 'endgameFx') {
    const fxKey = (item.data?.fxKey as string | undefined) ?? 'default';
    const meta = FINISHER_META[fxKey] ?? FINISHER_META.default!;
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 12,
          background: `radial-gradient(circle at 50% 35%, ${meta.color}33, rgba(0,0,0,0.4))`,
          border: `1px solid ${meta.color}66`,
          boxShadow: `inset 0 0 16px ${meta.color}33`,
          fontSize: size * 0.5,
          lineHeight: 1,
        }}
        title={item.name}
      >
        <span style={{ filter: `drop-shadow(0 0 6px ${meta.color})` }}>{meta.emoji}</span>
      </div>
    );
  }

  if (item.slot === 'victoryShout') {
    const text = (item.data?.text as string | undefined) ?? item.name;
    return (
      <div
        style={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.35)',
          border: `1px solid ${tint}66`,
          borderRadius: 10,
          padding: 4,
          textAlign: 'center',
          fontFamily: 'var(--f-display)',
          fontSize: 9,
          letterSpacing: '0.04em',
          color: tint,
          overflow: 'hidden',
          lineHeight: 1.1,
        }}
        title={text}
      >
        {text.slice(0, 14)}
      </div>
    );
  }

  if (item.slot === 'avatarFrame') {
    const glow = (item.data?.glow as string | undefined) ?? null;
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          border: `3px solid ${tint}`,
          boxShadow: glow ? `0 0 14px ${glow}, inset 0 0 12px ${glow}66` : 'none',
          background: 'rgba(0,0,0,0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: size * 0.6,
            height: size * 0.6,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2a2670, #1a1448)',
          }}
        />
      </div>
    );
  }

  return (
    <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden>
      {item.slot === 'hat' && <HatGlyph tint={tint} />}
      {item.slot === 'body' && <BodyGlyph tint={tint} />}
      {item.slot === 'cape' && <CapeGlyph tint={tint} />}
    </svg>
  );
}

function HatGlyph({ tint }: { tint: string }) {
  return (
    <g>
      {/* Brim */}
      <ellipse cx="20" cy="28" rx="15" ry="3" fill="rgba(0,0,0,0.4)" />
      {/* Crown */}
      <path
        d="M 9 28 Q 9 12 20 10 Q 31 12 31 28 Z"
        fill={tint}
        stroke="rgba(0,0,0,0.4)"
        strokeWidth="1.2"
      />
      {/* Highlight */}
      <path d="M 12 22 Q 14 14 20 13" stroke="rgba(255,255,255,0.45)" strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Logo dot */}
      <circle cx="20" cy="20" r="2.5" fill="rgba(255,255,255,0.9)" />
    </g>
  );
}

function BodyGlyph({ tint }: { tint: string }) {
  return (
    <g>
      {/* Torso */}
      <path
        d="M 10 12 L 30 12 L 32 22 L 28 22 L 28 34 L 12 34 L 12 22 L 8 22 Z"
        fill={tint}
        stroke="rgba(0,0,0,0.4)"
        strokeWidth="1.2"
      />
      {/* Highlight */}
      <rect x="13" y="14" width="4" height="14" rx="1.5" fill="rgba(255,255,255,0.35)" />
      {/* Number badge */}
      <circle cx="20" cy="22" r="3.4" fill="rgba(255,255,255,0.85)" />
    </g>
  );
}

function CapeGlyph({ tint }: { tint: string }) {
  if (!tint || tint === 'null') {
    return (
      <g>
        <text
          x="20"
          y="26"
          textAnchor="middle"
          fontSize="10"
          fill="rgba(245,243,255,0.4)"
          fontFamily="Bungee,Impact"
        >
          NONE
        </text>
      </g>
    );
  }
  return (
    <g>
      {/* Cape */}
      <path
        d="M 12 8 L 28 8 L 34 30 L 28 34 L 20 32 L 12 34 L 6 30 Z"
        fill={tint}
        stroke="rgba(0,0,0,0.4)"
        strokeWidth="1.2"
      />
      {/* Folds */}
      <path d="M 18 10 L 16 32" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
      <path d="M 24 10 L 26 32" stroke="rgba(0,0,0,0.35)" strokeWidth="1" />
      <path d="M 14 10 L 14 32" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
    </g>
  );
}
